'use client'

import { OB, FONT_MONO, Icon, PaprMark } from './kit'

/**
 * Shared site chrome (top nav + footer) used by the landing AND every standalone
 * page (marketing, legal, blog, help), so all pages have an identical menu and
 * footer. Fully self-contained: it injects its own hover/responsive CSS (see
 * SiteChromeStyles) rather than depending on the landing's `.papr-funnel`
 * FunnelStyles — otherwise the nav loses its hover states and mobile behaviour
 * on any page that isn't the landing.
 *
 * Links use real hrefs (/#features etc.) so they work from any page — on the
 * landing the hash smooth-scrolls (globals.css), elsewhere it navigates home.
 */

const NAV = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Roadmap', href: '/roadmap' },
]

const FOOTER_COLS: { title: string; links: { label: string; href: string }[] }[] = [
  { title: 'Product', links: [{ label: 'Features', href: '/#features' }, { label: 'Pricing', href: '/#pricing' }, { label: 'Roadmap', href: '/roadmap' }] },
  { title: 'Company', links: [{ label: 'About', href: '/about' }, { label: 'Careers', href: '/careers' }, { label: 'Contact', href: '/contact' }] },
  { title: 'Resources', links: [{ label: 'Help center', href: '/help' }, { label: 'Blog', href: '/blog' }] },
  { title: 'Legal', links: [{ label: 'Privacy', href: '/privacy' }, { label: 'Terms', href: '/terms' }, { label: 'Security', href: '/security' }, { label: 'DPA', href: '/dpa' }] },
]

// Social URLs pending — kept as # for now (per owner).
const SOCIALS = [
  { label: 'X (Twitter)', glyph: 'X', href: '#' },
  { label: 'LinkedIn', glyph: 'in', href: '#' },
  { label: 'GitHub', glyph: 'gh', href: '#' },
]

// Self-contained styles for the chrome — dedicated `sn-*` classes so behaviour
// is identical on every page (no dependency on FunnelStyles / `.papr-funnel`).
// Static string → dangerouslySetInnerHTML (no XSS surface; avoids a hydration
// text-mismatch on the media-query characters).
const CHROME_CSS = `
.sn-link { transition: background .12s ease, color .12s ease; }
.sn-link:hover { background: rgba(0,0,0,0.04); color: #0a0a0b; }
.sn-cta:hover { filter: brightness(0.92); }
.sn-cta:active { transform: translateY(1px); }
.sn-social:hover { border-color: rgba(0,0,0,0.28); color: #0a0a0b; }
@media (max-width: 720px) { .sn-navlinks { display: none !important; } }
`

function SiteChromeStyles() {
  return <style dangerouslySetInnerHTML={{ __html: CHROME_CSS }} />
}

export function SiteNav() {
  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(243,240,234,0.82)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${OB.line}` }}>
      <SiteChromeStyles />
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: 28 }}>
        <a href="/" aria-label="Papr home" style={{ display: 'inline-flex', textDecoration: 'none' }}>
          <PaprMark accent={OB.ink} dot={OB.purple} size={21} />
        </a>
        <nav className="sn-navlinks" style={{ display: 'flex', gap: 4 }}>
          {NAV.map((l) => (
            <a key={l.label} href={l.href} className="sn-link" style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13.5, color: OB.mute, textDecoration: 'none', fontWeight: 500 }}>
              {l.label}
            </a>
          ))}
        </nav>
        <div style={{ flex: 1 }} />
        <a href="/auth/signin" style={{ fontSize: 13.5, color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
        <a href="/auth/register" className="sn-cta" style={{ height: 38, padding: '0 16px', borderRadius: 10, border: 'none', background: OB.ink, color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}>
          Start for free <Icon d="M5 12h14M13 6l6 6-6 6" size={14} color="#fff" />
        </a>
      </div>
    </header>
  )
}

export function SiteFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${OB.line}`, background: OB.paper }}>
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '52px 28px 28px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40 }}>
          <div style={{ flex: '1 1 260px', minWidth: 220, maxWidth: 340 }}>
            <PaprMark accent={OB.ink} dot={OB.purple} size={22} />
            <p style={{ fontSize: 13.5, color: OB.mute, lineHeight: 1.55, margin: '14px 0 18px', maxWidth: 290 }}>
              Get paid, without the chasing. Papr drafts, sends, and politely chases your invoices so cash lands on time.
            </p>
            <div style={{ display: 'flex', gap: 9 }}>
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} aria-label={s.label} className="sn-social" style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${OB.line2}`, background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: OB.mute, fontFamily: FONT_MONO, fontSize: 12, fontWeight: 600, letterSpacing: '-0.02em' }}>
                  {s.glyph}
                </a>
              ))}
            </div>
          </div>
          <div style={{ flex: '2 1 520px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 28 }}>
            {FOOTER_COLS.map((col) => (
              <div key={col.title}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: OB.ink, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{col.title}</div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="sn-link" style={{ display: 'inline-block', margin: '-3px -8px', padding: '3px 8px', borderRadius: 6, fontSize: 13.5, color: OB.mute, textDecoration: 'none', fontWeight: 500 }}>{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 40, paddingTop: 22, borderTop: `1px solid ${OB.line}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <PaprMark accent={OB.mute} dot={OB.purple} size={15} />
          <span style={{ fontSize: 12, color: OB.dim, fontFamily: FONT_MONO }}>© 2026 Papr · Paprhq Inc.</span>
          <div style={{ flex: 1, minWidth: 16 }} />
          <span style={{ fontSize: 12, color: OB.dim, fontFamily: FONT_MONO }}>Made for freelancers &amp; small businesses</span>
        </div>
      </div>
    </footer>
  )
}
